/**
 * The rules screen.
 *
 * EVERYTHING BEHIND IT HAS EXISTED FOR MONTHS. The Sieve compiler, the drift
 * detector, the ManageSieve client and the three `/rules` routes were all built
 * and all tested, and until today nothing supplied the origin with a rules store
 * so every route answered 503. This is the surface, and it is deliberately the
 * SIMPLE half of `drafts/rules.md` — the builder, not the Sieve Studio.
 *
 * ## Two things it shows that a rules screen usually does not
 *
 * DRIFT. The script on the server is the authority on what runs; the model here
 * is the authority on what was meant. They come apart when somebody edits the
 * script by hand, and the origin can now tell the difference between "the model
 * moved" and "somebody edited the file" — so this says which, and offers the
 * matching choice rather than a generic "out of sync".
 *
 * THE COMPILED SIEVE. `POST /rules/check` compiles without installing, so the
 * reader can see exactly what will be written before it is. `interaction.md`
 * asks for actions to be explicable as protocol; for a rule, the protocol IS
 * the artefact, and hiding it would be hiding the thing itself.
 *
 * ## And one it refuses to do
 *
 * "Run on existing mail" is a DRY RUN FIRST, always, with the count in front of
 * the reader before anything moves. The origin defaults to that and this asks
 * for it explicitly; a rule that matches four thousand messages is a rule whose
 * author should see the number before it files them.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { api, HearthError, type DriftState, type GuiRule, type MailboxState, type RuleSet } from './api.ts'

/**
 * The armed revert behind the last activation, as `GET /rules` reports it.
 *
 * WHY THIS SCREEN CARES. Saving rules overwrites the Sieve script the mail
 * server is actually running, and until recently the only way back from a bad
 * one was the operator's nightly restic snapshot — a thing nobody in this
 * household can reach. The origin now copies the outgoing script before it
 * overwrites it and arms a window; this is the window, and the banner below is
 * the only place a person can see or use it.
 */
interface PendingRevert {
  readonly changeId: string
  readonly state: 'armed' | 'reverting'
  /** Unix SECONDS, like every other time on this surface. */
  readonly expiresAt: number
  readonly previousActive: string | null
  readonly backupName: string | null
  /** Why the last automatic attempt failed, when one has. */
  readonly lastError?: string
}

/** What a retroactive run left behind, so it can be taken back. */
interface RunUndo {
  readonly runId: string
  readonly ruleName: string
  readonly moved: number
  /** Unix seconds. The window is the SERVER'S; a reload must not lose it. */
  readonly undoableUntil: number
  /** Moves the server gave no COPYUID for, which cannot be put back. */
  readonly notUndoable: number
  /**
   * The rule also set a flag, and putting the messages back does not unset it.
   *
   * THE INVERSE IS A MOVE AND ONLY A MOVE. The origin journals one row per
   * COPYUID landing and nothing else, so a rule that files AND marks read is
   * undone by half — and "Put them back" is a sentence that promises the whole
   * of it. Saying so is what a reader needs to decide whether pressing it is
   * enough; journalling the flags would be the other answer, and a bigger one.
   */
  readonly flagsKept: boolean
}

/** How long is left, as a person reads a clock. */
function untilText(deadline: number, now: number): string {
  const seconds = Math.max(0, deadline - now)
  if (seconds >= 3600) {
    const hours = Math.round(seconds / 3600)
    return hours === 1 ? 'an hour' : `${hours} hours`
  }
  if (seconds >= 60) {
    const minutes = Math.round(seconds / 60)
    return minutes === 1 ? 'a minute' : `${minutes} minutes`
  }
  return `${seconds} seconds`
}

export interface RulesProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly mailboxes: readonly MailboxState[]
  readonly onError: (message: string) => void
}

/** A new, empty rule. ULID-shaped enough for a key nobody parses. */
const blankRule = (): GuiRule => ({
  kind: 'gui',
  id: `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`,
  name: 'New rule',
  enabled: true,
  match: { type: 'group', id: 'g', op: 'all', children: [] },
  actions: [],
})

/**
 * The fields the simple builder offers, and what each one reads.
 *
 * `verb` IS PART OF THE ROW AND NOT DECORATION. Every field here used to be a
 * `contains`, so the word between the two controls could be a constant; two of
 * them are not, and a row reading "If Spam score contains 6" would be a
 * sentence about a test the server is not running.
 */
const FIELDS = [
  { value: 'from', label: 'From', verb: 'contains' },
  { value: 'to', label: 'To', verb: 'contains' },
  /**
   * PLUS-ADDRESSING AS A FIRST-CLASS TEST.
   *
   * `me+petco@example.com` is how a household hands out one address per
   * correspondent, and matching the tag with `To contains petco` also matches
   * `me+petcolumbia@` and anyone whose display name says Petco. The compiler
   * emits `address :detail`, which is RFC 5233's `subaddress` — advertised by
   * this server, and required only by rules that ask for it.
   */
  { value: 'to_detail', label: 'To, after the +', verb: 'is' },
  { value: 'subject', label: 'Subject', verb: 'contains' },
  { value: 'body', label: 'Body', verb: 'contains' },
  { value: 'list_id', label: 'Mailing list', verb: 'contains' },
  /**
   * THE SPAM SCORE, WHICH THE COMPILER COULD NOT EMIT SAFELY UNTIL NOW.
   *
   * It compiled to `spamtest`, the one extension `decisions.md` says must never
   * be enabled here — so any rule set carrying one of these was refused WHOLE by
   * the server, taking every other rule in it down too. It is an ordinary
   * comparison against Rspamd's `X-Spam-Score` header now, so the field can be
   * offered at all.
   */
  { value: 'spam_score', label: 'Spam score', verb: 'is at least' },
] as const

type FieldValue = (typeof FIELDS)[number]['value']

const verbFor = (field: FieldValue): string => FIELDS.find((f) => f.value === field)?.verb ?? 'contains'

const conditionFor = (id: string, field: FieldValue, value: string) => {
  if (field === 'subject' || field === 'body') {
    return { type: 'condition' as const, id, source: { kind: 'text' as const, field }, operator: 'contains' as const, value }
  }
  if (field === 'list_id') {
    return { type: 'condition' as const, id, source: { kind: 'meta' as const, field: 'list_id' as const }, operator: 'contains' as const, value }
  }
  if (field === 'spam_score') {
    // `ge`, AND THE VALUE IS A NUMBER. `Number('')` is 0, which as a threshold
    // means "every message" — so an empty box compiles to a rule that files
    // everything, which is the one answer this control must never give quietly.
    // The compiler floors it; sending a string would make it `NaN` there.
    return {
      type: 'condition' as const,
      id,
      source: { kind: 'meta' as const, field: 'spam_score' as const },
      operator: 'ge' as const,
      // `Number('') || 0` WAS 0, AND 0 MEANS EVERY MESSAGE. Ordinary mail scores
      // negative, so a threshold of zero files most of an inbox — and an empty
      // box, or a field switched from "From contains billing@" where the value
      // is still text, produced exactly that silently. `NaN` is what a
      // non-number is, and the compiler refuses it by name instead.
      value: value.trim() === '' ? Number.NaN : Number(value),
    }
  }
  if (field === 'to_detail') {
    // `:is` AND NOT `:contains`. The whole point of testing the detail alone is
    // that it is exact: a substring test on the tag would let `petco` match
    // `petcolumbia`, which is the imprecision this field exists to remove.
    return {
      type: 'condition' as const,
      id,
      source: { kind: 'address' as const, field: 'to' as const, part: 'detail' as const },
      operator: 'is' as const,
      value,
    }
  }
  return { type: 'condition' as const, id, source: { kind: 'address' as const, field }, operator: 'contains' as const, value }
}

/** Read a condition back into the two controls that produced it. */
function readCondition(condition: unknown): { field: FieldValue; value: string } {
  const c = condition as { source?: { kind?: string; field?: string; part?: string }; value?: unknown }
  const raw = Array.isArray(c.value) ? String(c.value[0] ?? '') : String(c.value ?? '')
  const source = c.source
  if (source?.kind === 'text') return { field: (source.field as FieldValue) ?? 'subject', value: raw }
  if (source?.kind === 'meta') {
    return { field: source.field === 'spam_score' ? 'spam_score' : 'list_id', value: raw }
  }
  // THE PART IS READ BACK TOO, or editing a plus-address rule would silently
  // rewrite it into a substring match on the whole To header the moment
  // somebody typed in the value box.
  if (source?.part === 'detail') return { field: 'to_detail', value: raw }
  return { field: (source?.field as FieldValue) ?? 'from', value: raw }
}

/**
 * The action that files into `name`, carrying this server's SPECIAL-USE flag
 * when the mailbox has one.
 *
 * A RULE THAT FILES BY NAME STOPS FIRING WHEN THE FOLDER IS RENAMED, and that
 * rename happens in Thunderbird or on a phone, where nothing knows Hearth has a
 * rule pointing at the old name. `fileinto :specialuse "\\Trash" "Trash"` files
 * by the flag and falls back to the name, so the rule survives — which is what
 * "no folder is special-cased by name" means once it reaches the script.
 *
 * NOTHING IS ASKED OF THE READER. The flag is a property of the mailbox they
 * picked, already in `/mailboxes` because the sync recorded it; a checkbox would
 * be asking somebody to make a decision about IMAP.
 */
const fileintoFor = (name: string, mailboxes: readonly MailboxState[]) => {
  const special = mailboxes.find((box) => box.name === name)?.specialUse
  return { type: 'fileinto' as const, mailbox: name, ...(special ? { specialUse: special } : {}) }
}

export function Rules({ open, onClose, mailboxes, onError }: RulesProps): React.JSX.Element | null {
  const ids = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)

  const [ruleSet, setRuleSet] = useState<RuleSet | null>(null)
  const [drift, setDrift] = useState<DriftState | null>(null)
  const [script, setScript] = useState<string | null>(null)
  /**
   * The compiler's refusal, when the preview could not be built at all.
   *
   * WITHOUT IT THE PREVIEW JUST DISAPPEARS. `update()` cleared `script` on any
   * failure, so a rule the origin refuses — an empty spam-score box, say —
   * looked exactly like a rule that had not been checked yet: the "what gets
   * written to the server" block silently stopped being there, and the reader
   * found out at Save. The origin sends a sentence and the id of the rule it is
   * about; both are shown, and the id is what puts the sentence on the right
   * card rather than floating it over a list of four.
   */
  const [refusal, setRefusal] = useState<{ message: string; ruleId: string | null } | null>(null)
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState<{ ruleId: string; matched: number; search: string } | null>(null)
  const [pending, setPending] = useState<PendingRevert | null>(null)
  const [undo, setUndo] = useState<RunUndo | null>(null)

  const load = useCallback(() => {
    api
      .rules()
      .then((result) => {
        setRuleSet(result.ruleSet)
        setDrift(result.drift)
        // THE WINDOW COMES OFF THE SERVER AND IS NOT REMEMBERED HERE. It
        // survives a reload, a second device and a restarted origin precisely
        // because this screen never invents it. `api.rules()` is typed against
        // the two fields it has always returned, so the third is read off the
        // response rather than by widening a type this file does not own.
        setPending((result as { pending?: PendingRevert }).pending ?? null)
      })
      .catch((error: unknown) => {
        // A 503 here is the one worth naming precisely: it means the origin has
        // no ManageSieve connection, not that rules are broken.
        onError(
          error instanceof HearthError && error.code === 'not_configured'
            ? 'This server has no connection to the mail server, so rules cannot be read or changed'
            : error instanceof Error
              ? error.message
              : 'Could not load your rules',
        )
      })
  }, [onError])

  useEffect(() => {
    if (open) load()
  }, [open, load])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    const panel = panelRef.current
    const opener = document.activeElement
    if (opener instanceof HTMLElement && !panel?.contains(opener)) returnTo.current = opener
    panel?.focus()
    return () => returnTo.current?.focus()
  }, [open])

  const update = (next: RuleSet): void => {
    setRuleSet(next)
    // The compiled form follows every edit, so "what will be written" is never
    // a stale answer to a question about the rule in front of you.
    api
      .checkRules(next)
      .then((result) => {
        setScript(result.script ?? null)
        setRefusal(null)
      })
      .catch((error: unknown) => {
        setScript(null)
        // ONLY `bad_rule` BECOMES A SENTENCE ON THE FORM. Every other failure
        // here is about the network or the mail server and says nothing about
        // the rule being edited; putting "could not reach the mail server"
        // beside a condition would send the reader to fix a rule that is fine.
        setRefusal(
          error instanceof HearthError && error.code === 'bad_rule'
            ? {
                message: error.message,
                ruleId: typeof error.body?.ruleId === 'string' ? error.body.ruleId : null,
              }
            : null,
        )
      })
  }

  const save = async (): Promise<void> => {
    if (!ruleSet) return
    setBusy(true)
    try {
      await api.saveRules(ruleSet)
      // `load()` RE-READS, so the trial banner appears on the same round trip
      // that installed the script rather than on the next time somebody opens
      // this screen. That matters: the window it announces is already running.
      load()
    } catch (error) {
      // AND ONTO THE FORM, not only into a toast that scrolls away. A rule set
      // loaded from the store is never previewed — `load()` reads it, it does
      // not compile it — so a bad value saved by an older build reaches Save
      // with no refusal on screen, and the toast would be the only place it was
      // ever said.
      if (error instanceof HearthError && error.code === 'bad_rule') {
        setRefusal({
          message: error.message,
          ruleId: typeof error.body?.ruleId === 'string' ? error.body.ruleId : null,
        })
      }
      onError(error instanceof Error ? error.message : 'Those rules could not be saved')
    } finally {
      setBusy(false)
    }
  }

  /** Keep the rules that were just installed, and stop the countdown. */
  const keepRules = async (): Promise<void> => {
    if (!pending) return
    setBusy(true)
    try {
      const answer = await api.confirmRules(pending.changeId)
      if (answer.kept) {
        setPending(null)
        return
      }
      /**
       * A 200 THAT MEANS NOTHING WAS KEPT, AND THE BANNER MUST NOT GO.
       *
       * The origin answers 200 with `kept: false` in two cases and this treated
       * both as success. `nothing_pending` is the window having ALREADY EXPIRED
       * AND REVERTED — so the reader pressed "Keep these filters", was told
       * nothing, watched the banner disappear, and now believes their rules are
       * installed when they have been put back. `superseded` is a confirm from a
       * banner about an older activation, on a second device: clearing that
       * screen would hide a window that is still counting down.
       *
       * `load()` RATHER THAN A GUESS. The server knows which window is armed;
       * re-reading is how the banner comes back saying the true one, or goes
       * away because there really is nothing pending.
       */
      onError(
        answer.reason === 'superseded'
          ? 'Those filters were replaced by a newer save, which is the one now on trial.'
          : 'That trial window had already closed, so the previous filters were put back.',
      )
      load()
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Those rules could not be confirmed')
    } finally {
      setBusy(false)
    }
  }

  /** Put the previous script back now, rather than waiting for the window. */
  const revertRules = async (): Promise<void> => {
    setBusy(true)
    try {
      await api.revertRules()
      // RE-READ RATHER THAN GUESSED. A revert restores the rule set as well as
      // the script, and the model this screen is editing has to become the
      // restored one — otherwise the next Save would push the bad rules back.
      load()
    } catch (error) {
      onError(error instanceof Error ? error.message : 'The previous script could not be put back')
    } finally {
      setBusy(false)
    }
  }

  const runOnExisting = async (ruleId: string, apply: boolean): Promise<void> => {
    setBusy(true)
    try {
      const result = await api.applyRule(ruleId, !apply)
      if (apply) {
        onError(`Filed ${result.applied.toLocaleString()} messages`)
        setPreview(null)
        /**
         * AND THE WAY BACK, WHICH THIS SCREEN USED TO DROP ON THE FLOOR.
         *
         * The origin computed a real COPYUID landing for every message it moved
         * and threw all of them away into a count; the one bulk write in this
         * product with no undo was the biggest one it has. It journals them now
         * and answers with a `runId`, and this is the only place a person can
         * reach it.
         *
         * THE DEADLINE IS THE SERVER'S, not a timer started here. Somebody who
         * has just moved four thousand messages closes the laptop and thinks
         * about it; a countdown that died with the tab would be an undo that
         * only exists while nobody needs it.
         */
        const run = result as unknown as {
          runId?: string
          undoableUntil?: number
          undoable?: number
          notUndoable?: number
        }
        setUndo(
          run.runId
            ? {
                runId: run.runId,
                ruleName: result.ruleName,
                moved: run.undoable ?? result.applied,
                undoableUntil: run.undoableUntil ?? 0,
                notUndoable: run.notUndoable ?? 0,
                // READ OFF THE RULE THAT WAS JUST RUN, which this screen is
                // holding: the origin's answer counts moves and says nothing
                // about flags, and asking it to would be a second field meaning
                // what this one already knows.
                flagsKept: (ruleSet?.rules ?? []).some(
                  (candidate) =>
                    candidate.id === ruleId &&
                    candidate.kind === 'gui' &&
                    candidate.actions.some((action) => action.type === 'addflag' || action.type === 'setflag'),
                ),
              }
            : null,
        )
      } else {
        setPreview({ ruleId, matched: result.matched, search: result.search })
      }
    } catch (error) {
      // A REFUSAL HERE IS INFORMATION, not a failure. The origin refuses a rule
      // IMAP SEARCH cannot express faithfully, and the reason names the
      // condition — which is more useful than "could not run".
      onError(error instanceof Error ? error.message : 'That rule could not be run over existing mail')
    } finally {
      setBusy(false)
    }
  }

  /** Move back everything one retroactive run moved. */
  const undoRun = async (): Promise<void> => {
    if (!undo) return
    setBusy(true)
    try {
      const result = await api.undoRuleRun(undo.runId)
      setUndo(null)
      /**
       * THE WHOLE ANSWER, BECAUSE THERE IS NO SECOND ATTEMPT.
       *
       * The origin deletes the journal before it moves anything — deliberately,
       * so two clicks race in SQLite rather than in the mail server — which
       * means whatever this call does not put back is not going to be put back
       * by pressing the button again. It answers four numbers about that, and
       * this read one: a reader whose mail server refused three hundred of four
       * hundred moves was told "Put back 100 messages" and nothing else.
       *
       * FOUR FACTS WITH FOUR REMEDIES, and they are said separately because
       * they are not the same problem. `failed` is the mail server refusing now
       * and the first reason is worth quoting; `notUndone` is a move the server
       * never gave a landing for; `renumbered` is a mailbox recreated under the
       * journal, so those messages are somewhere, under UIDs nothing here can
       * name.
       */
      const trouble: string[] = []
      if (result.failed) {
        trouble.push(
          `${result.failed.toLocaleString()} were refused by your mail server${result.firstError ? `: ${result.firstError}` : ''}`,
        )
      }
      if (result.notUndone) {
        trouble.push(
          `${result.notUndone.toLocaleString()} could not be put back: your mail server did not say where it had moved them`,
        )
      }
      if (result.renumbered) {
        trouble.push(
          `${result.renumbered.toLocaleString()} were in a folder that has been recreated since, so they could not be found`,
        )
      }
      onError(
        trouble.length === 0
          ? `Put back ${result.restored.toLocaleString()} messages`
          : `Put back ${result.restored.toLocaleString()} messages. ${trouble.join('. ')}. Pressing it again will not ` +
            `retry them — the record of the run is gone.`,
      )
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Those messages could not be put back')
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  const rules = (ruleSet?.rules ?? []).filter((rule): rule is GuiRule => rule.kind === 'gui')

  return (
    <div
      className="settings-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className="settings-panel rules-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${ids}-title`}
        tabIndex={-1}
        ref={panelRef}
      >
        <div className="settings-head">
          <h2 className="settings-title" id={`${ids}-title`}>
            Filters
          </h2>
          <button className="settings-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {/*
          THE TRIAL BANNER, ABOVE THE DRIFT ONE.
          Drift is a report about the past ("somebody edited the script"); this
          is a decision waiting to be made about the present, and a decision
          outranks a report. Both can be on screen at once — saving over a
          hand-edited script produces exactly that pair — and burying the two
          buttons under a paragraph about drift would hide the only way back.
        */}
        {pending && (
          // `rules-drift` IS BORROWED AND THE SECOND CLASS IS THE HOOK. This is
          // the same object as the drift banner — a full-width strip at the top
          // of the panel saying something is not settled — so it takes that
          // styling rather than shipping a second one that will drift from it.
          <div className="rules-drift rules-trial" role="status">
            <div>
              {/*
                "after", NOT A COUNTDOWN TO ZERO. The origin closes this window
                on the next request any of this household's devices makes once
                it is out of date — there is no wall clock ticking on the
                server — so a display counting to 00:00 would promise a
                precision that does not exist. It is honest about the deadline
                and vague about the second, which is the right way round.
              */}
              These filters are on trial. Unless you keep them, Hearth puts the
              previous script back after {untilText(pending.expiresAt, Math.floor(Date.now() / 1000))}
              {pending.previousActive ? ` (back to “${pending.previousActive}”)` : ' (back to no filters at all)'}.
            </div>
            {pending.lastError && (
              // A FAILED AUTOMATIC REVERT MUST NOT BE SILENT, or this banner
              // would sit here for ever with nothing saying the mail server is
              // refusing it and nobody knowing to press the button by hand.
              <div className="rules-trial-error">
                Hearth already tried to put it back and could not: {pending.lastError}
              </div>
            )}
            <div className="rules-preview-actions">
              <button type="button" disabled={busy} onClick={() => void keepRules()}>
                Keep these filters
              </button>
              <button type="button" disabled={busy} onClick={() => void revertRules()}>
                Undo now
              </button>
            </div>
          </div>
        )}

        {undo && (
          <div className="rules-drift rules-undo" role="status">
            <div>
              Filed <strong>{undo.moved.toLocaleString()}</strong> messages with “{undo.ruleName}”.
              {undo.notUndoable > 0 &&
                ` ${undo.notUndoable.toLocaleString()} of them cannot be put back: your mail server did not say where it moved them.`}
              {/*
                THE DEADLINE, WHICH THE ORIGIN SETS AND THIS SCREEN USED TO
                THROW AWAY. `undoableUntil` arrived on the response, was put in
                state, and was read by nothing — so the button sat there looking
                permanent and stopped working half an hour later with no warning
                and no explanation. It is the server's clock and not a timer
                started here, for the same reason the trial banner's is:
                somebody who has just filed four thousand messages closes the
                laptop and thinks about it.
              */}
              {undo.undoableUntil > 0 &&
                ` You can put them back for the next ${untilText(undo.undoableUntil, Math.floor(Date.now() / 1000))}.`}
              {/* WHAT PUTTING THEM BACK DOES NOT DO. The journal holds moves;
                  a flag the rule set stays set, and the button's own label
                  promises more than that. */}
              {undo.flagsKept && ' The flags it set stay set.'}
            </div>
            <div className="rules-preview-actions">
              <button type="button" disabled={busy} onClick={() => void undoRun()}>
                Put them back
              </button>
              <button type="button" onClick={() => setUndo(null)}>
                Dismiss
              </button>
            </div>
          </div>
        )}

        {drift && drift.state !== 'clean' && (
          <p className="rules-drift" role="status">
            {/*
              'unknown' IS TESTED FIRST AND EXPLICITLY, because this chain ends
              in an `else` that makes a CLAIM. Let a fourth state fall through
              and the reader is told "the rules here have not been saved to the
              server yet" — a confident sentence about a comparison that never
              happened, offering a Save as the fix, in the one situation where
              nothing can be saved at all.
            */}
            {drift.state === 'unknown'
              ? 'Hearth could not reach the mail server, so it cannot tell whether the script running there still matches these rules.'
              : drift.state === 'unmanaged'
                ? 'There is a Sieve script on the server that Hearth did not write. Saving here will replace it.'
                : (drift as { edited?: boolean }).edited
                  ? 'The script on the server has been edited by hand since Hearth wrote it. Saving here will overwrite those edits.'
                  : 'The rules here have not been saved to the server yet.'}
          </p>
        )}

        <div className="rules-list">
          {rules.length === 0 && <p className="rules-empty">No filters yet.</p>}
          {rules.map((rule, index) => {
            const first = rule.match.type === 'group' ? rule.match.children[0] : undefined
            const { field, value } = readCondition(first ?? {})
            const fileinto = rule.actions.find((action) => action.type === 'fileinto')
            const replaceRule = (next: GuiRule): void => {
              const nextRules = [...(ruleSet?.rules ?? [])]
              nextRules[index] = next
              update({ version: 1, ...(ruleSet ?? {}), rules: nextRules })
            }

            return (
              <div className="rules-rule" key={rule.id}>
                <div className="rules-row">
                  <input
                    className="rules-name"
                    aria-label="Rule name"
                    value={rule.name}
                    onChange={(event) => replaceRule({ ...rule, name: event.target.value })}
                  />
                  <label className="rules-enabled">
                    <input
                      type="checkbox"
                      checked={rule.enabled}
                      onChange={(event) => replaceRule({ ...rule, enabled: event.target.checked })}
                    />
                    On
                  </label>
                  <button
                    type="button"
                    className="rules-delete"
                    aria-label={`Delete ${rule.name}`}
                    onClick={() =>
                      update({
                        version: 1,
                        ...(ruleSet ?? {}),
                        rules: (ruleSet?.rules ?? []).filter((candidate) => candidate.id !== rule.id),
                      })
                    }
                  >
                    Delete
                  </button>
                </div>

                <div className="rules-row">
                  <span className="rules-word">If</span>
                  <select
                    aria-label="Field"
                    value={field}
                    onChange={(event) =>
                      replaceRule({
                        ...rule,
                        match: {
                          type: 'group',
                          id: 'g',
                          op: 'all',
                          children: [conditionFor(`${rule.id}-c`, event.target.value as FieldValue, value)],
                        },
                      })
                    }
                  >
                    {FIELDS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className="rules-word">{verbFor(field)}</span>
                  <input
                    aria-label="Value"
                    className="rules-value"
                    // A SPAM SCORE IS A NUMBER AND THE KEYBOARD SHOULD SAY SO;
                    // everything else here is text and `type="number"` would
                    // refuse a perfectly good sender address.
                    type={field === 'spam_score' ? 'number' : 'text'}
                    value={value}
                    onChange={(event) =>
                      replaceRule({
                        ...rule,
                        match: {
                          type: 'group',
                          id: 'g',
                          op: 'all',
                          children: [conditionFor(`${rule.id}-c`, field, event.target.value)],
                        },
                      })
                    }
                  />
                </div>

                <div className="rules-row">
                  <span className="rules-word">File into</span>
                  <select
                    aria-label="Mailbox"
                    value={fileinto?.type === 'fileinto' ? fileinto.mailbox : ''}
                    onChange={(event) =>
                      replaceRule({
                        ...rule,
                        actions: event.target.value ? [fileintoFor(event.target.value, mailboxes)] : [],
                      })
                    }
                  >
                    <option value="">nothing</option>
                    {mailboxes.map((box) => (
                      <option key={box.name} value={box.name}>
                        {box.name}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="rules-run" disabled={busy} onClick={() => void runOnExisting(rule.id, false)}>
                    Run on existing mail…
                  </button>
                </div>

                {refusal?.ruleId === rule.id && (
                  // `role="alert"` AND NOT `role="status"`: the rule in front of
                  // this reader will not be installed, and a polite live region
                  // would leave a screen-reader user editing a form that is
                  // already refused.
                  <p className="rules-refusal" role="alert">
                    {refusal.message}
                  </p>
                )}

                {preview?.ruleId === rule.id && (
                  <div className="rules-preview" role="status">
                    <p>
                      This matches <strong>{preview.matched.toLocaleString()}</strong> messages already in your
                      mailboxes.
                    </p>
                    {/* THE SEARCH ITSELF. interaction.md asks for actions to be
                        explicable as protocol, and this is the sentence somebody
                        about to move four thousand messages deserves. */}
                    <code>{preview.search}</code>
                    <div className="rules-preview-actions">
                      <button type="button" onClick={() => setPreview(null)}>
                        Cancel
                      </button>
                      <button type="button" disabled={busy} onClick={() => void runOnExisting(rule.id, true)}>
                        File them
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* THE ORPHAN CASE, and it is not hypothetical: the compiler names the
            rule it refused, but a refusal raised over the rule set as a whole
            carries no id, and one naming a rule that has since been deleted
            matches no card either. Either way the sentence still has to be
            somewhere a person can read it, so it lands here — where the script
            it is standing in for would have been. */}
        {refusal && !(ruleSet?.rules ?? []).some((rule) => rule.id === refusal.ruleId) && (
          <p className="rules-refusal" role="alert">
            {refusal.message}
          </p>
        )}

        {script && (
          <details className="rules-script">
            <summary>What gets written to the server</summary>
            <pre>{script}</pre>
          </details>
        )}

        <div className="compose-actions">
          <button
            type="button"
            className="compose-cancel"
            onClick={() => update({ version: 1, ...(ruleSet ?? {}), rules: [...(ruleSet?.rules ?? []), blankRule()] })}
          >
            Add a filter
          </button>
          <button type="button" className="compose-send" disabled={busy || !ruleSet} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
